<template>
  <div class="recommendations-carousel">
    <splide
      class="recommendations-carousel__carousel"
      :has-track="false"
      :options="config"
    >
      <div class="recommendations-carousel__header container">
        <heading-copy
          :classes="heading.classes"
          :content="{
            headingText: heading.heading,
            textColour: 'dark',
          }"
        />

        <div class="recommendations-carousel__arrows splide__arrows">
          <button
            class="splide__arrow splide__arrow--prev"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <icon-chevron-left />
          </button>

          <button
            class="splide__arrow splide__arrow--next"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <icon-chevron-right />
          </button>
        </div>
      </div>

      <splide-track class="container">
        <template v-if="state === 'loading'">
          <splide-slide>
            <product-card-simple
              :product="false"
            />
          </splide-slide>
        </template>

        <template v-else>
          <splide-slide
            v-for="(product, index) of products"
            :key="`recommendations-carousel-${liquid.sectionId}-${index}`"
          >
            <product-card-simple
              :product="product"
            />
          </splide-slide>
        </template>
      </splide-track>

      <ul class="recommendations-carousel__pagination splide__pagination" />
    </splide>
  </div>
</template>

<script>

/**
 * Vue: Recommendations carousel (recommendations-carousel)
 * -----------------------------------------------------------------------------
 * Renders a carousel of product cards with associated header using data
 * from Shopify Collections or Bloomreach Recommendations.
 *
 * @param {String} bloomreachRecommendationId - Bloomreach recommendation ID.
 * @param {Boolean|Object} collection - Shopify formatted collection object.
 * @param {Object} config - Splide config.
 * @param {Object} heading - Settings for heading-copy.
 * @param {String} initialRecommendationsState - Initial vue loading state.
 * @param {Number} limit - Max number of products to display.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} productDataSource - Pull products from Shopify or Bloomreach.
 * @param {Number} referenceProductId - Product ID to pass to Bloomreach.
 * @param {Boolean} useCache - If products should be cached.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import { formatBloomreachProduct, getBloomreachRecommendations } from '~/helpers/bloomreach'

import IconChevronLeft from '~icons/directional-navigation/chevron/left.svg'
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'

import HeadingCopy from '~async/heading-copy/heading-copy'
import ProductCardSimple from '~global/product-card-simple/product-card-simple'

export default {
  name: 'RecommendationsCarousel',

  components: {
    HeadingCopy,
    IconChevronLeft,
    IconChevronRight,
    ProductCardSimple,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    bloomreachRecommendationId: {
      type: String,
      default: '',
    },
    collection: {
      type: [Boolean, Object],
      default: false,
    },
    config: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    heading: {
      type: Object,
      default: () => ({
        classes: {},
        heading: '',
      }),
    },
    initialRecommendationsState: {
      type: String,
      default: 'loading',
    },
    limit: {
      type: Number,
      default: 8,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    productDataSource: {
      type: String,
      default: 'shopify',
      validator: (value) => ['bloomreach', 'shopify'].includes(value),
    },
    referenceProductId: {
      type: Number,
      default: 0,
    },
    useCache: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      products: [],
      state: this.initialRecommendationsState,
    }
  },

  mounted() {
    this.initialiseProducts()
  },

  methods: {

    /**
     * Initialise `products` array.
     * - Attempt to read from session storage data before formatting Shopify
     *   collection data, or running a Bloomreach API query.
     */
    initialiseProducts() {
      try {
        const cachedData = JSON.parse(sessionStorage.getItem(
          `cnvs-recommendations-carousel-${this.liquid.sectionId}`,
        ))

        if (
          !this.useCache ||
          !cachedData ||
          cachedData.products.length === 0 ||
          cachedData.productDataSource !== this.productDataSource
        ) {
          this.populateProducts()
          return
        }

        this.setProducts(cachedData.products)

      } catch (error) {
        this.populateProducts()
        reportError('Failed to retrieve Bloomreach products from session storage', error)
      }
    },

    /**
     * Pull product data from Shopify, or run a Bloomreach API call to populate
     * `products` array.
     */
    populateProducts() {
      if (this.productDataSource === 'shopify') {
        this.setProducts(this.collection.products.slice(0, this.limit))
        return
      }

      this.queryBloomreachProducts()
    },

    /**
     * Get formatted Bloomreach products.
     * @returns {Array}
     */
    queryBloomreachProducts() {
      if (!this.bloomreachRecommendationId) {
        return
      }

      const recommendationsPayload = {
        recommendationId: this.bloomreachRecommendationId,
        size: this.limit,
        callback: this.formatBloomreachResponse,
        fillWithRandom: true,
      }

      // https://documentation.bloomreach.com/engagement/docs/personalization#getrecommendation
      if (this.referenceProductId) {
        recommendationsPayload.items = { [JSON.stringify(this.referenceProductId)]: 1 }
      }

      getBloomreachRecommendations(recommendationsPayload)
    },

    /**
     * Format Bloomreach response.
     * @param {Array} response - Bloomreach API response.
     */
    formatBloomreachResponse(response) {
      if (!response) {
        return
      }

      const formattedProducts = response.map((product) => {
        return formatBloomreachProduct(product)
      })

      this.setProducts(formattedProducts)
    },

    /**
     * Set products array based on section settings.
     * @param {Array} productArray - Array of product objects.
     */
    setProducts(productArray) {
      this.products = productArray
      this.state = 'ready'

      if (!this.useCache) {
        return
      }

      const dataToCache = {
        productDataSource: this.productDataSource,
        products: this.products,
      }

      sessionStorage.setItem(
        `cnvs-recommendations-carousel-${this.liquid.sectionId}`,
        JSON.stringify(dataToCache),
      )
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './recommendations-carousel';
</style>
